import { addDays, formatMonthYear, monthBounds } from '../format/date.js';
import { round2 } from './money.js';
import type { InvoicePeriod } from './invoice-schedule.js';
import type { InvoiceLineContract } from './invoicing.js';

/**
 * Application operations ("Applikations drift"): the monthly fee for running a customer's
 * application, billed by the automatic invoicing on the draft that closes a month.
 *
 * Where it comes from (decided 02-10-2026):
 *
 *   whether   a won contract in CRM with an `Applikations drift - <tier>` product line
 *   price     the price on the CRM line - see `crmLineUnitPrice` - or, when it has none, the
 *             Business Central item with the same name (Lille, Standard, Business, Enterprise)
 *   when      the automatic draft for a period that closes a month - made on the 1st of the next
 *             month, or the Monday after on a weekly plan - carries the **coming** month: the
 *             draft of 1 November bills November. "1. januar for januar", as the original notes
 *             put it.
 *             A manual invoice on /fakturering/ny created on the 1st carries the same month
 *             (decided 02-10-2026). Whichever comes first bills it; the other finds it billed.
 *
 * The line is booked on its own BC item and is not hours: readers that count hours or an hourly
 * revenue skip it - see `StoredInvoiceLine.application_operations` in the API.
 *
 * `App - …` lines on a CRM contract and the subscriptions created in Codepass are billed the same
 * way, on the same lines - see subscription.ts (decided 07-10-2026).
 */

/** A CRM product or BC item that is application operations. Trimmed, any case. */
export function isApplicationOperationsProduct(name: string | null | undefined): boolean {
  return /^applikations\s*drift\b/i.test(name?.trim() ?? '');
}

/**
 * The month (`YYYY-MM`) a draft for the period bills application operations for, or null.
 *
 * Only a period that contains the last day of a month carries it - both halves of a half-month
 * plan are drafts, but only the second closes the month. The month is the one after that day:
 * application operations are billed in advance.
 *
 * **Contains, not ends on**, for the weekly plan: its weeks seldom end on a month's last day. The
 * week from 26 October to 1 November 2026 closes October, so its draft - made Monday 2 November -
 * bills November. Every month is closed by exactly one week, so it is billed once.
 */
export function applicationOperationsMonth(period: InvoicePeriod): string | null {
  const close = monthBounds(period.start).end;
  if (close > period.end) return null;
  return addDays(close, 1).slice(0, 7);
}

/**
 * The month (`YYYY-MM`) a manual invoice created on `date` bills application operations for, or
 * null. Only on the 1st, and then the month it is created in - the same month the automatic draft
 * of that day bills.
 */
export function applicationOperationsMonthOn(date: string): string | null {
  return /^\d{4}-\d{2}-01$/.test(date) ? date.slice(0, 7) : null;
}

/** `november 2026`. */
export function applicationOperationsMonthLabel(month: string): string {
  const [year, monthNo] = month.split('-').map(Number);
  /* Built from the parts, not parsed: `new Date('2026-11')` is UTC and can land in October. */
  return formatMonthYear(new Date(year ?? Number.NaN, (monthNo ?? Number.NaN) - 1, 1));
}

/**
 * The price per unit of a subscription line on a CRM contract.
 *
 * **A won contract is locked: the customer agreed to the price on the line**, and it holds when
 * the BC item's price changes later (decided 23-10-2026). ONC-1006 was signed with `Applikations
 * drift - Lille` at 600 kr; the item now costs 900. `amount_excl_vat` is the line's
 * `extendedamount` - what the customer pays, after any discount - since `priceperunit` cannot be
 * read (see `linePrice` in the API).
 *
 * The BC item's price on a contract not yet won, and on a won line without an amount.
 */
export function crmLineUnitPrice(
  line: { quantity: number; amount_excl_vat: number },
  won: boolean,
  itemPrice: number,
): number {
  return won && line.quantity > 0 && line.amount_excl_vat > 0
    ? round2(line.amount_excl_vat / line.quantity)
    : itemPrice;
}

/** The customer-facing line text: `Applikations drift - Lille, november 2026`. */
export function applicationOperationsDescription(product: string, month: string): string {
  return `${product.trim()}, ${applicationOperationsMonthLabel(month)}`;
}

/**
 * The key a billed month is remembered under: the CRM contract and the month. One invoice per
 * contract and month - see `invoicedApplicationOperations` in the API.
 */
export function applicationOperationsKey(contractId: string, month: string): string {
  return `${contractId.trim().toLowerCase()}|${month}`;
}

/**
 * One subscription line on an invoice: application operations or an app on a CRM contract, or a
 * subscription created in Codepass - see subscription.ts.
 */
export interface ApplicationOperationsLine {
  /** The CRM contract the product is on. Null on a subscription created in Codepass. */
  contract: InvoiceLineContract | null;
  /** The subscription in `codepass_subscriptions`. Left out on a CRM line. */
  subscription_id?: string;
  /** The product on the CRM contract or the subscription, and the BC item of that name. */
  product: string;
  /** The month the line bills - the first, when it bills more than one. `YYYY-MM`. */
  month: string;
  /** How many months the line bills. Left out: one - a CRM line is always one month. */
  months?: number;
  /**
   * Customer-facing - see `applicationOperationsDescription` and `subscriptionDescription`.
   */
  description: string;
  /** The CRM line's quantity, or the months on a Codepass subscription. */
  quantity: number;
  /** The CRM line's price - see `crmLineUnitPrice` - or the subscription's price per month. */
  unit_price_excl_vat: number;
  total_excl_vat: number;
  /** The BC item the line is booked on. */
  bc_item_id: string;
}
